import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { SlidingPanel } from '@wix/wix-ui-premium-purchase';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { TransitionProps } from 'react-transition-group/Transition';
import { PlansPage } from '../PlansPage';
import { SubscriptionPage } from '../../../pages/SubscriptionPage/SubscriptionPage';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { Experiments, PackagePickerType } from 'enums';
import { Faq, FaqType } from '../../Faq';
import { StudioFaq } from '../../StudioFaq/StudioFaq';
import { AppController } from '../../App/App.controller';
import s from '../../App/App.scss';
import layoutStyles from './AppLayout.scss';
import { CheckoutPage } from '../../../pages/CheckoutPage/CheckoutPage';
import { Title } from '../Title/Title';
import { RequestCallModal } from 'components/RequestCall/Modal/RequestCallModal';
import { Box } from '@wix/design-system';
import { useEnterpriseModalContext } from 'providers/EnterpriseModalContextProvider';
import { AnonymousFooterBanner } from '../AnonymousFooterBanner/AnonymousFooterBanner';
import { SubHeader } from '../SubHeader';
import { StudioMigrationBanner } from '../StudioMigrationBanner';

export interface AppLayoutProps extends InjectedExperimentsProps {
  rootStore: RootStore;
}
const animationTimeouts: TransitionProps['timeout'] = {
  enter: 600,
  exit: 300,
};

const AppLayoutComponent: React.FC<AppLayoutProps> = (props) => {
  const controller = React.useMemo(
    () => new AppController(props),
    [props.rootStore.appStore],
  );
  const { onCycleSelect, onSelectProduct, fetchOrderPageFunction } = controller;
  const { isModalOpen, clickSource, closeModal } = useEnterpriseModalContext();

  const { theme } = usePageModeContext();
  const { experiments } = props;

  const {
    currentStep,
    handleOnBackButtonClick,
    isWizardHidden,
    isDowngradePlanOfDeclineChargeService,
    appContext: { biLogger, packagePickerType, currentPlanDetails },
    isAnonymous,
    isSecondUpgrade,
    isChangePlan,
    removeUnloadListener,
  } = props.rootStore.appStore;

  if (isAnonymous) {
    removeUnloadListener();
  }

  return (
    <div className={classNames(s.headerAndContent, theme)}>
      <Box
        direction='vertical'
        width='100%'
        align='center'
        flexGrow={1}
        gap='30px'
        marginTop='24px'
      >
        {packagePickerType === PackagePickerType.EditorXToStudio && (
          <StudioMigrationBanner
            isAnonymous={isAnonymous}
            isUpgrade={isSecondUpgrade}
            isChangePlan={isChangePlan}
            currentPlanDetails={currentPlanDetails}
            biLogger={biLogger}
          />
        )}
        <Box marginTop={isAnonymous ? 'SP2' : ''}>
          <SubHeader />
        </Box>
      </Box>
      <Title
        currentStep={currentStep}
        showBackButton={!isWizardHidden}
        onBack={handleOnBackButtonClick}
        isSecondStepDisabled={isDowngradePlanOfDeclineChargeService}
      />
      <div
        className={classNames(
          layoutStyles.contentContainer,
          s.transitionContainer,
          s.panel,
          s.withBackgroundColor,
          theme,
        )}
      >
        <SlidingPanel
          activeChildIndex={currentStep}
          timeout={animationTimeouts}
        >
          <PlansPage
            className={layoutStyles.plansPageContainer}
            onSelectProduct={onSelectProduct}
          />
          <SubscriptionPage
            className={layoutStyles.subscriptionContainer}
            data-hook='subscription-page'
            onCycleSelect={onCycleSelect}
          />
          <CheckoutPage
            className={layoutStyles.checkoutContainer}
            fetchOrderPageData={fetchOrderPageFunction}
          />
        </SlidingPanel>
        {experiments.enabled(Experiments.FaqCommonComponent) ? (
          <StudioFaq />
        ) : (
          <Faq faqType={FaqType.Default} />
        )}
        {isAnonymous && <AnonymousFooterBanner />}
        <RequestCallModal
          onCancelClick={() => {
            biLogger.logRequestCallPopupCTA({
              clickType: 'close',
            });
            closeModal();
          }}
          isOpen={isModalOpen}
          clickSource={clickSource}
        />
      </div>
    </div>
  );
};

export const AppLayout = withInjectedRootStore(
  withExperiments(observer(AppLayoutComponent)),
);
